import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from '../../core/services/api/api.service';
import { StorageKeys } from '../../enums/storage-keys.enum';
import {
  BRAND_PALETTE_DEFAULTS,
  DEFAULT_PALETTE_ID,
  DEFAULT_PALETTE_NAME,
} from '../config/brand-palette.config';

export interface ThemePaletteOption {
  id: string;
  name: string;
  preview: string;
  appThemeEl23Light: string;
  appThemeEl23Dark: string;
  appThemeG900Light: string;
  appThemeG900Dark: string;
  pulseRateColor: string;
  temperatureColor: string;
  spO2Color: string;
  respiratoryRateColor: string;
  bloodPressureColor: string;
  bloodPressure2Color: string;
  pulseRateAreaStartColor: string;
  pulseRateAreaEndColor: string;
  temperatureAreaStartColor: string;
  temperatureAreaEndColor: string;
  spO2AreaStartColor: string;
  spO2AreaEndColor: string;
  respiratoryRateAreaStartColor: string;
  respiratoryRateAreaEndColor: string;
  bloodPressureAreaStartColor: string;
  bloodPressureAreaEndColor: string;
  bloodPressure2AreaStartColor: string;
  bloodPressure2AreaEndColor: string;
  toastColor: string;
}

export interface UpsertThemePaletteRequest {
  id?: string;
  name: string;
  preview: string;
  appThemeEl23Light: string;
  appThemeEl23Dark: string;
  appThemeG900Light: string;
  appThemeG900Dark: string;
  pulseRateColor?: string;
  temperatureColor?: string;
  spO2Color?: string;
  respiratoryRateColor?: string;
  bloodPressureColor?: string;
  bloodPressure2Color?: string;
  pulseRateAreaStartColor?: string;
  pulseRateAreaEndColor?: string;
  temperatureAreaStartColor?: string;
  temperatureAreaEndColor?: string;
  spO2AreaStartColor?: string;
  spO2AreaEndColor?: string;
  respiratoryRateAreaStartColor?: string;
  respiratoryRateAreaEndColor?: string;
  bloodPressureAreaStartColor?: string;
  bloodPressureAreaEndColor?: string;
  bloodPressure2AreaStartColor?: string;
  bloodPressure2AreaEndColor?: string;
  toastColor?: string;
}

interface ApiColorPaletteDto {
  Id?: string;
  Name?: string;
  Preview?: string;
  AppThemeEl23Light?: string;
  AppThemeEl23Dark?: string;
  AppThemeG900Light?: string;
  AppThemeG900Dark?: string;
  PulseRateColor?: string;
  TemperatureColor?: string;
  SpO2Color?: string;
  RespiratoryRateColor?: string;
  BloodPressureColor?: string;
  BloodPressure2Color?: string;
  PulseRateAreaStartColor?: string;
  PulseRateAreaEndColor?: string;
  TemperatureAreaStartColor?: string;
  TemperatureAreaEndColor?: string;
  SpO2AreaStartColor?: string;
  SpO2AreaEndColor?: string;
  RespiratoryRateAreaStartColor?: string;
  RespiratoryRateAreaEndColor?: string;
  BloodPressureAreaStartColor?: string;
  BloodPressureAreaEndColor?: string;
  BloodPressure2AreaStartColor?: string;
  BloodPressure2AreaEndColor?: string;
  ToastColor?: string;
  id?: string;
  name?: string;
  preview?: string;
  appThemeEl23Light?: string;
  appThemeEl23Dark?: string;
  appThemeG900Light?: string;
  appThemeG900Dark?: string;
  pulseRateColor?: string;
  temperatureColor?: string;
  spO2Color?: string;
  respiratoryRateColor?: string;
  bloodPressureColor?: string;
  bloodPressure2Color?: string;
  pulseRateAreaStartColor?: string;
  pulseRateAreaEndColor?: string;
  temperatureAreaStartColor?: string;
  temperatureAreaEndColor?: string;
  spO2AreaStartColor?: string;
  spO2AreaEndColor?: string;
  respiratoryRateAreaStartColor?: string;
  respiratoryRateAreaEndColor?: string;
  bloodPressureAreaStartColor?: string;
  bloodPressureAreaEndColor?: string;
  bloodPressure2AreaStartColor?: string;
  bloodPressure2AreaEndColor?: string;
  toastColor?: string;
}

@Injectable({ providedIn: 'root' })
export class ThemePaletteService {
  palettes: ThemePaletteOption[] = [];
  private readonly defaultPaletteId = DEFAULT_PALETTE_ID;

  private readonly fallbackPalettes: ThemePaletteOption[] = [
    {
      id: DEFAULT_PALETTE_ID,
      name: DEFAULT_PALETTE_NAME,
      ...BRAND_PALETTE_DEFAULTS,
    },
  ];

  private isLoaded = false;

  private readonly endpoint = `${environment.apiLocations.identity}/app-config/color-palettes`;
  private readonly addColorPaletteEndpoint = `${environment.apiLocations.identity}/users/addcolorpalette`;
  private readonly updateColorPaletteEndpoint = `${environment.apiLocations.identity}/users/updatecolorpalette`;

  constructor(private apiService: ApiService) {}

  async ensurePalettesLoaded(): Promise<void> {
    if (this.isLoaded) return;

    try {
      const response = await firstValueFrom(
        this.apiService.get<
          ApiColorPaletteDto[] | { items?: ApiColorPaletteDto[] }
        >(this.endpoint)
      );

      const data = response?.data;
      const rawList = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.items)
        ? (data as any).items
        : [];

      const mapped = rawList
        .map((item: ApiColorPaletteDto) => this.mapApiPalette(item))
        .filter((p: ThemePaletteOption | null): p is ThemePaletteOption => !!p);

      this.palettes = mapped.length > 0 ? mapped : [...this.fallbackPalettes];
    } catch {
      this.palettes = [...this.fallbackPalettes];
    } finally {
      this.isLoaded = true;
    }
  }

  async refreshPalettes(): Promise<void> {
    this.isLoaded = false;
    await this.ensurePalettesLoaded();
  }

  async createPalette(request: UpsertThemePaletteRequest): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.apiService.post(this.endpoint, this.toApiUpsertRequest(request))
      );
      const isSuccess = !response?.fail;
      if (isSuccess) {
        await this.refreshPalettes();
      }
      return isSuccess;
    } catch {
      return false;
    }
  }

  async updatePalette(request: UpsertThemePaletteRequest): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.apiService.put(this.endpoint, this.toApiUpsertRequest(request))
      );
      const isSuccess = !response?.fail;
      if (isSuccess) {
        await this.refreshPalettes();
        this.syncCurrentUserPaletteAfterPaletteUpdate(request.id);
      }
      return isSuccess;
    } catch {
      return false;
    }
  }

  async deletePalette(paletteId: string): Promise<boolean> {
    if (!paletteId) return false;

    try {
      const response = await firstValueFrom(
        this.apiService.delete(`${this.endpoint}/${paletteId}`)
      );
      const isSuccess = !response?.fail;
      if (isSuccess) {
        this.clearCurrentUserPaletteIfMatches(paletteId);
        await this.refreshPalettes();
      }
      return isSuccess;
    } catch {
      return false;
    }
  }

  hasSelection(_userId: string): boolean {
    return !!this.getCurrentUserPaletteId();
  }

  getSelectionId(_userId: string): string | null {
    return this.getCurrentUserPaletteId();
  }

  applySavedPalette(_userId: string): void {
    const selectedId = this.getCurrentUserPaletteId();
    const selected = selectedId
      ? this.palettes.find((p) => p.id === selectedId) ?? null
      : null;

    if (selected) {
      this.applyPalette(selected);
      return;
    }

    const user = this.getCurrentUser();
    const userPalette = this.mapApiPalette(
      user?.colorPalette ?? user?.ColorPalette
    );
    if (!userPalette) {
      const fallback = this.getDefaultPalette();
      if (fallback) this.applyPalette(fallback);
      return;
    }

    this.upsertPalette(userPalette);
    this.applyPalette(userPalette);
  }

  applyUserPalette(_userId: string, palette: ThemePaletteOption): void {
    this.upsertPalette(palette);
    this.setCurrentUserPalette(palette);
    this.applyPalette(palette);
  }

  async addPaletteAndApply(_userId: string, paletteId: string): Promise<void> {
    const selected = this.palettes.find((p) => p.id === paletteId);
    if (!selected) return;

    await this.addColorPalette(selected.id);
    this.setCurrentUserPalette(selected);
    this.applyPalette(selected);
  }

  async updatePaletteAndApply(
    _userId: string,
    paletteId: string
  ): Promise<void> {
    const selected = this.palettes.find((p) => p.id === paletteId);
    if (!selected) return;

    this.setCurrentUserPalette(selected);
    this.applyPalette(selected);
    await this.updateColorPalette(selected.id);
  }

  applyPaletteById(paletteId: string): void {
    const selected = this.palettes.find((p) => p.id === paletteId);
    if (selected) {
      this.applyPalette(selected);
      return;
    }

    const fallback = this.getDefaultPalette();
    if (fallback) this.applyPalette(fallback);
  }

  applyPalettePreview(palette: ThemePaletteOption): void {
    this.applyPalette(palette);
  }

  private applyPalette(palette: ThemePaletteOption): void {
    const root = document.documentElement;
    root.style.setProperty('--app-theme-el23-light', palette.appThemeEl23Light);
    root.style.setProperty('--app-theme-el23-dark', palette.appThemeEl23Dark);
    root.style.setProperty('--app-theme-g900-light', palette.appThemeG900Light);
    root.style.setProperty('--app-theme-g900-dark', palette.appThemeG900Dark);
    root.style.setProperty(
      '--app-palette-pulse-rate-color',
      palette.pulseRateColor
    );
    root.style.setProperty(
      '--app-palette-temperature-color',
      palette.temperatureColor
    );
    root.style.setProperty('--app-palette-spo2-color', palette.spO2Color);
    root.style.setProperty(
      '--app-palette-respiratory-rate-color',
      palette.respiratoryRateColor
    );
    root.style.setProperty(
      '--app-palette-blood-pressure-color',
      palette.bloodPressureColor
    );
    root.style.setProperty(
      '--app-palette-blood-pressure-2-color',
      palette.bloodPressure2Color
    );
    root.style.setProperty(
      '--app-palette-pulse-rate-area-start-color',
      palette.pulseRateAreaStartColor
    );
    root.style.setProperty(
      '--app-palette-pulse-rate-area-end-color',
      palette.pulseRateAreaEndColor
    );
    root.style.setProperty(
      '--app-palette-temperature-area-start-color',
      palette.temperatureAreaStartColor
    );
    root.style.setProperty(
      '--app-palette-temperature-area-end-color',
      palette.temperatureAreaEndColor
    );
    root.style.setProperty(
      '--app-palette-spo2-area-start-color',
      palette.spO2AreaStartColor
    );
    root.style.setProperty(
      '--app-palette-spo2-area-end-color',
      palette.spO2AreaEndColor
    );
    root.style.setProperty(
      '--app-palette-respiratory-rate-area-start-color',
      palette.respiratoryRateAreaStartColor
    );
    root.style.setProperty(
      '--app-palette-respiratory-rate-area-end-color',
      palette.respiratoryRateAreaEndColor
    );
    root.style.setProperty(
      '--app-palette-blood-pressure-area-start-color',
      palette.bloodPressureAreaStartColor
    );
    root.style.setProperty(
      '--app-palette-blood-pressure-area-end-color',
      palette.bloodPressureAreaEndColor
    );
    root.style.setProperty(
      '--app-palette-blood-pressure-2-area-start-color',
      palette.bloodPressure2AreaStartColor
    );
    root.style.setProperty(
      '--app-palette-blood-pressure-2-area-end-color',
      palette.bloodPressure2AreaEndColor
    );
    root.style.setProperty('--app-palette-toast-color', palette.toastColor);
  }

  mapApiPalette(
    raw: ApiColorPaletteDto | null | undefined
  ): ThemePaletteOption | null {
    if (!raw) return null;

    const id = raw.id ?? raw.Id;
    const name = raw.name ?? raw.Name;
    const preview = raw.preview ?? raw.Preview;
    const appThemeEl23Light = raw.appThemeEl23Light ?? raw.AppThemeEl23Light;
    const appThemeEl23Dark = raw.appThemeEl23Dark ?? raw.AppThemeEl23Dark;
    const appThemeG900Light = raw.appThemeG900Light ?? raw.AppThemeG900Light;
    const appThemeG900Dark = raw.appThemeG900Dark ?? raw.AppThemeG900Dark;
    const fallback = this.fallbackPalettes[0];
    const pulseRateColor =
      raw.pulseRateColor ?? raw.PulseRateColor ?? fallback.pulseRateColor;
    const temperatureColor =
      raw.temperatureColor ?? raw.TemperatureColor ?? fallback.temperatureColor;
    const spO2Color = raw.spO2Color ?? raw.SpO2Color ?? fallback.spO2Color;
    const respiratoryRateColor =
      raw.respiratoryRateColor ??
      raw.RespiratoryRateColor ??
      fallback.respiratoryRateColor;
    const bloodPressureColor =
      raw.bloodPressureColor ??
      raw.BloodPressureColor ??
      fallback.bloodPressureColor;
    const bloodPressure2Color =
      raw.bloodPressure2Color ??
      raw.BloodPressure2Color ??
      fallback.bloodPressure2Color;
    const pulseRateAreaStartColor =
      raw.pulseRateAreaStartColor ??
      raw.PulseRateAreaStartColor ??
      fallback.pulseRateAreaStartColor;
    const pulseRateAreaEndColor =
      raw.pulseRateAreaEndColor ??
      raw.PulseRateAreaEndColor ??
      fallback.pulseRateAreaEndColor;
    const temperatureAreaStartColor =
      raw.temperatureAreaStartColor ??
      raw.TemperatureAreaStartColor ??
      fallback.temperatureAreaStartColor;
    const temperatureAreaEndColor =
      raw.temperatureAreaEndColor ??
      raw.TemperatureAreaEndColor ??
      fallback.temperatureAreaEndColor;
    const spO2AreaStartColor =
      raw.spO2AreaStartColor ??
      raw.SpO2AreaStartColor ??
      fallback.spO2AreaStartColor;
    const spO2AreaEndColor =
      raw.spO2AreaEndColor ?? raw.SpO2AreaEndColor ?? fallback.spO2AreaEndColor;
    const respiratoryRateAreaStartColor =
      raw.respiratoryRateAreaStartColor ??
      raw.RespiratoryRateAreaStartColor ??
      fallback.respiratoryRateAreaStartColor;
    const respiratoryRateAreaEndColor =
      raw.respiratoryRateAreaEndColor ??
      raw.RespiratoryRateAreaEndColor ??
      fallback.respiratoryRateAreaEndColor;
    const bloodPressureAreaStartColor =
      raw.bloodPressureAreaStartColor ??
      raw.BloodPressureAreaStartColor ??
      fallback.bloodPressureAreaStartColor;
    const bloodPressureAreaEndColor =
      raw.bloodPressureAreaEndColor ??
      raw.BloodPressureAreaEndColor ??
      fallback.bloodPressureAreaEndColor;
    const bloodPressure2AreaStartColor =
      raw.bloodPressure2AreaStartColor ??
      raw.BloodPressure2AreaStartColor ??
      fallback.bloodPressure2AreaStartColor;
    const bloodPressure2AreaEndColor =
      raw.bloodPressure2AreaEndColor ??
      raw.BloodPressure2AreaEndColor ??
      fallback.bloodPressure2AreaEndColor;
    const toastColor = raw.toastColor ?? raw.ToastColor ?? fallback.toastColor;

    if (
      !id ||
      !name ||
      !preview ||
      !appThemeEl23Light ||
      !appThemeEl23Dark ||
      !appThemeG900Light ||
      !appThemeG900Dark
    ) {
      return null;
    }

    const palette: ThemePaletteOption = {
      id,
      name,
      preview,
      appThemeEl23Light,
      appThemeEl23Dark,
      appThemeG900Light,
      appThemeG900Dark,
      pulseRateColor,
      temperatureColor,
      spO2Color,
      respiratoryRateColor,
      bloodPressureColor,
      bloodPressure2Color,
      pulseRateAreaStartColor,
      pulseRateAreaEndColor,
      temperatureAreaStartColor,
      temperatureAreaEndColor,
      spO2AreaStartColor,
      spO2AreaEndColor,
      respiratoryRateAreaStartColor,
      respiratoryRateAreaEndColor,
      bloodPressureAreaStartColor,
      bloodPressureAreaEndColor,
      bloodPressure2AreaStartColor,
      bloodPressure2AreaEndColor,
      toastColor,
    };

    return this.applyFrontendDefaultPaletteOverrides(palette);
  }

  private upsertPalette(palette: ThemePaletteOption): void {
    const exists = this.palettes.some((p) => p.id === palette.id);
    if (!exists) {
      this.palettes = [...this.palettes, palette];
    }
  }

  private getDefaultPalette(): ThemePaletteOption | null {
    return (
      this.palettes.find((p) => p.id === this.defaultPaletteId) ??
      this.fallbackPalettes.find((p) => p.id === this.defaultPaletteId) ??
      this.palettes[0] ??
      this.fallbackPalettes[0] ??
      null
    );
  }

  private applyFrontendDefaultPaletteOverrides(
    palette: ThemePaletteOption
  ): ThemePaletteOption {
    if (palette.id !== this.defaultPaletteId) return palette;

    return {
      ...palette,
      ...BRAND_PALETTE_DEFAULTS,
    };
  }

  private getCurrentUser(): any | null {
    const userStr = localStorage.getItem(StorageKeys.USER);
    return userStr ? JSON.parse(userStr) : null;
  }

  private saveCurrentUser(user: any): void {
    localStorage.setItem(StorageKeys.USER, JSON.stringify(user));
  }

  private getCurrentUserPaletteId(): string | null {
    const user = this.getCurrentUser();
    const raw = user?.colorPalette ?? user?.ColorPalette;
    if (!raw) return null;
    return raw.id ?? raw.Id ?? null;
  }

  private setCurrentUserPalette(palette: ThemePaletteOption): void {
    const user = this.getCurrentUser();
    if (!user) return;

    const mapped = {
      id: palette.id,
      name: palette.name,
      preview: palette.preview,
      appThemeEl23Light: palette.appThemeEl23Light,
      appThemeEl23Dark: palette.appThemeEl23Dark,
      appThemeG900Light: palette.appThemeG900Light,
      appThemeG900Dark: palette.appThemeG900Dark,
      pulseRateColor: palette.pulseRateColor,
      temperatureColor: palette.temperatureColor,
      spO2Color: palette.spO2Color,
      respiratoryRateColor: palette.respiratoryRateColor,
      bloodPressureColor: palette.bloodPressureColor,
      bloodPressure2Color: palette.bloodPressure2Color,
      pulseRateAreaStartColor: palette.pulseRateAreaStartColor,
      pulseRateAreaEndColor: palette.pulseRateAreaEndColor,
      temperatureAreaStartColor: palette.temperatureAreaStartColor,
      temperatureAreaEndColor: palette.temperatureAreaEndColor,
      spO2AreaStartColor: palette.spO2AreaStartColor,
      spO2AreaEndColor: palette.spO2AreaEndColor,
      respiratoryRateAreaStartColor: palette.respiratoryRateAreaStartColor,
      respiratoryRateAreaEndColor: palette.respiratoryRateAreaEndColor,
      bloodPressureAreaStartColor: palette.bloodPressureAreaStartColor,
      bloodPressureAreaEndColor: palette.bloodPressureAreaEndColor,
      bloodPressure2AreaStartColor: palette.bloodPressure2AreaStartColor,
      bloodPressure2AreaEndColor: palette.bloodPressure2AreaEndColor,
      toastColor: palette.toastColor,
    };

    user.colorPalette = mapped;
    user.ColorPalette = mapped;
    this.saveCurrentUser(user);
  }

  private clearCurrentUserPaletteIfMatches(paletteId: string): void {
    const user = this.getCurrentUser();
    if (!user) return;

    const raw = user?.colorPalette ?? user?.ColorPalette;
    const selectedId = raw?.id ?? raw?.Id;
    if (!selectedId || selectedId !== paletteId) return;

    user.colorPalette = null;
    user.ColorPalette = null;
    this.saveCurrentUser(user);
  }

  private async addColorPalette(paletteId: string): Promise<void> {
    try {
      await firstValueFrom(
        this.apiService.post(this.addColorPaletteEndpoint, {
          PaletteId: paletteId,
        })
      );
    } catch {}
  }

  private async updateColorPalette(paletteId: string): Promise<void> {
    try {
      await firstValueFrom(
        this.apiService.put(this.updateColorPaletteEndpoint, {
          PaletteId: paletteId,
        })
      );
    } catch {}
  }

  private toApiUpsertRequest(request: UpsertThemePaletteRequest): any {
    return {
      Id: request.id,
      Name: request.name,
      Preview: request.preview,
      AppThemeEl23Light: request.appThemeEl23Light,
      AppThemeEl23Dark: request.appThemeEl23Dark,
      AppThemeG900Light: request.appThemeG900Light,
      AppThemeG900Dark: request.appThemeG900Dark,
      PulseRateColor: request.pulseRateColor,
      TemperatureColor: request.temperatureColor,
      SpO2Color: request.spO2Color,
      RespiratoryRateColor: request.respiratoryRateColor,
      BloodPressureColor: request.bloodPressureColor,
      BloodPressure2Color: request.bloodPressure2Color,
      PulseRateAreaStartColor: request.pulseRateAreaStartColor,
      PulseRateAreaEndColor: request.pulseRateAreaEndColor,
      TemperatureAreaStartColor: request.temperatureAreaStartColor,
      TemperatureAreaEndColor: request.temperatureAreaEndColor,
      SpO2AreaStartColor: request.spO2AreaStartColor,
      SpO2AreaEndColor: request.spO2AreaEndColor,
      RespiratoryRateAreaStartColor: request.respiratoryRateAreaStartColor,
      RespiratoryRateAreaEndColor: request.respiratoryRateAreaEndColor,
      BloodPressureAreaStartColor: request.bloodPressureAreaStartColor,
      BloodPressureAreaEndColor: request.bloodPressureAreaEndColor,
      BloodPressure2AreaStartColor: request.bloodPressure2AreaStartColor,
      BloodPressure2AreaEndColor: request.bloodPressure2AreaEndColor,
      ToastColor: request.toastColor,
    };
  }

  private syncCurrentUserPaletteAfterPaletteUpdate(
    paletteId: string | undefined
  ): void {
    if (!paletteId) return;

    const selectedPaletteId = this.getCurrentUserPaletteId();
    if (!selectedPaletteId || selectedPaletteId !== paletteId) return;

    const updatedPalette = this.palettes.find((p) => p.id === paletteId);
    if (!updatedPalette) return;

    this.setCurrentUserPalette(updatedPalette);
    this.applyPalette(updatedPalette);
  }
}
